import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import QRCode from 'react-qr-code';
import { Button, Input, ModalWindow, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { useParticipantWebAppUrl } from 'libs.react_internal';
import { LogEvents } from '../../../constants/log_events.constants';
import { getPatientAppNonWebUrls } from '../../../helpers/study.helpers';
import { useCopyToClipboard } from '../../../hooks/use_copy_to_clipboard';
import { useCurrentEnvironment } from '../../../hooks/use_current_environment';
export const PatientAppInfoPopup = ({ isOpen, onClose, onLogEvent }) => {
    const translate = useLocalizeMessage();
    const [, copy] = useCopyToClipboard();
    const { environment } = useCurrentEnvironment();
    const { addNotification } = useToastNotification();
    const { participantWebAppUrl } = useParticipantWebAppUrl({
        cortexUrl: environment?.cortexURL,
        code: environment?.code,
    });
    const [currentIndex, setCurrentIndex] = useState(0);
    const contents = useMemo(() => {
        let paiOSUrl = '';
        let paAndroidUrl = '';
        if (environment?.cortexURL) {
            const nonWebUrls = getPatientAppNonWebUrls(environment.cortexURL);
            paiOSUrl = nonWebUrls.ios;
            paAndroidUrl = nonWebUrls.android;
        }
        return [
            {
                type: 'pa-web',
                title: translate('Medable for participants - Web URL'),
                url: participantWebAppUrl || '',
            },
            {
                type: 'pa-ios',
                title: translate('Medable for participants - iOS App'),
                url: paiOSUrl,
            },
            {
                type: 'pa-android',
                title: translate('Medable for participants - Android App'),
                url: paAndroidUrl,
            },
        ];
    }, [environment?.cortexURL, participantWebAppUrl]);
    const currentContent = useMemo(() => contents[currentIndex], [contents, currentIndex]);
    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? contents.length - 1 : prev - 1));
    };
    const handleNext = () => {
        setCurrentIndex((prev) => (prev === contents.length - 1 ? 0 : prev + 1));
    };
    const handleCopyToClipboard = (url) => {
        copy(url);
        onLogEvent(LogEvents.PATIENT_APP_URL_COPIED, { copiedUrl: url });
        addNotification({
            title: translate('Medable for participants URL copied to clipboard'),
            type: 'success',
        });
    };
    return (_jsx(ModalWindow, { title: translate('Medable for participants'), isOpen: isOpen, closeWindow: onClose, hideCloseIcon: false, footerClassNames: 'hidden', children: _jsxs("div", { className: 'flex flex-col items-center gap-4 justify-between h-full', children: [_jsx(Text, { className: 'text-lg font-bold w-full text-left', children: currentContent.title }), _jsxs("div", { className: 'w-full', children: [_jsxs("div", { className: 'flex items-center justify-between gap-4 w-full', children: [_jsx(Button, { size: 'xs', label: '', onClick: handlePrevious, iconName: 'chevronLeft', variant: 'secondary', disabled: contents.length <= 1 }), _jsx(QRCode, { value: currentContent.url, size: 400, level: 'H', "data-testid": 'qr-code-svg', "data-value": currentContent.url }), _jsx(Button, { size: 'xs', label: '', onClick: handleNext, iconName: 'chevronRight', variant: 'secondary', disabled: contents.length <= 1 })] }), contents.length > 1 && (_jsx("div", { className: 'flex items-center justify-center gap-2 mt-4', children: contents.map((content, index) => (_jsx("button", { onClick: () => setCurrentIndex(index), className: `w-2 h-2 rounded-full transition-colors ${index === currentIndex ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'}`, "aria-label": `Go to slide ${index + 1}` }, content.type))) }))] }), _jsxs("div", { className: 'w-full flex items-center justify-space-between gap-2', children: [_jsx("div", { className: 'w-full', children: _jsx(Input, { value: currentContent.url, id: `${currentContent.type}-url-input`, readOnly: true, width: 'full', type: 'text' }) }), _jsx(Button, { label: '', onClick: () => handleCopyToClipboard(currentContent.url), iconName: 'clipboardDocumentCheck' })] })] }) }));
};
